/**
 * FinancePanel - the project page's ACTUALS band: As Sold figures and the
 * PO / invoiced / remaining tiles, shown off one fetch of
 * GET /api/finance/project/:id.
 *
 * The figures were imported once from finance's old tracking workbooks; those
 * are retired (10/1/26, GlobalOps is the source of truth) and nothing is
 * imported from them again, so the UI never names a sheet, a workbook or a
 * file. Nothing here is ever written back anywhere. Per-engineer SELL vs COST
 * rates are not shown (or served) at all since 10/5/26.
 *
 * Someone else's finance edit, or an edit of this project's row (its PO
 * numbers say which lines are its own), re-reads it (r50 live events),
 * quietly: a re-read that fails keeps the figures on screen.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { fmt, upTo2 } from '../../utils/format';
import { leftoverOf } from '../../utils/projectMoney';
import { latestReads, matchRecord, useLiveRefresh } from '../../live/useLive';

const hrs = (v) => (v == null || v === 0 ? '-' : upTo2(Number(v)));
const when = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-');

export default function FinancePanel({ projectId, project = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // the project on screen, and its reads: an answer for another project, or
  // an older one landing after a newer, never shows
  const shownId = useRef(projectId);
  shownId.current = projectId;
  const [reads] = useState(latestReads);
  // `quiet` (a live re-read): an answer that is not the figures keeps what is shown
  const load = useCallback(
    (quiet) => {
      let live = true;
      const t = reads.start();
      fetch(`/api/finance/project/${encodeURIComponent(projectId)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!live || shownId.current !== projectId || (quiet === true && !d) || !reads.show(t)) return;
          setData(d);
          setLoading(false);
        })
        .catch(() => {
          if (live && quiet !== true) setLoading(false);
        });
      return () => {
        live = false;
      };
    },
    [projectId, reads]
  );
  useEffect(() => {
    setLoading(true);
    return load();
  }, [load]);
  // someone else's finance edit (any: its events do not say which project),
  // or of this project's own row
  const ownRow = matchRecord('/api', projectId);
  // (and the invoice ledger: an invoice or payment on the Finance Invoice tab)
  useLiveRefresh(
    ['finance', 'projects', 'finance-invoices'],
    () => {
      load(true);
    },
    { match: (e) => e.kind === 'finance' || e.kind === 'finance-invoices' || ownRow(e) }
  );

  if (loading) return null;
  const sheets = (data && data.sheets) || [];
  if (sheets.length === 0) {
    // No recorded actuals. The band still shows the PO/invoicing facts on the
    // project row itself. Remaining uses leftoverOf() - the same derivation
    // as the projects table ($ left over, else PO - invoiced) - so the two
    // views can never disagree.
    if (project) {
      const poVal = Number(project.poValue ?? project.po_value) || 0;
      const inv = Number(project.invoicedAlready ?? project.invoiced_already) || 0;
      const rawLeft = leftoverOf(project);
      const leftN = rawLeft == null ? (poVal ? 0 : null) : rawLeft;
      if (!poVal && !inv && leftN == null) return null;
      const tiles = [
        { label: 'PO Value', value: poVal ? fmt(poVal) : '-', color: '#0D0D0D' },
        { label: 'Invoiced', value: inv ? fmt(inv) : '-', color: '#1565C0' },
        {
          label: 'Remaining on PO',
          value: leftN != null ? fmt(leftN) : '-',
          color: leftN > 0 ? '#92400E' : '#1B5E20',
          bg: leftN > 0 ? '#FEF3C7' : '#DCEDC8',
        },
      ];
      return (
        <div style={{ marginTop: 12, background: '#F3F7F1', border: '1px solid #DCE7D6', borderRadius: 10, padding: '10px 12px 12px' }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: '#557052', letterSpacing: 1.6, textTransform: 'uppercase', marginBottom: 8 }}>
            Actuals
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {tiles.map((t) => (
              <div key={t.label} style={{ background: t.bg || '#fff', border: '1px solid #DCE7D6', borderRadius: 8, padding: '10px 14px' }}>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: '#557052',
                    letterSpacing: 1.4,
                    textTransform: 'uppercase',
                    marginBottom: 4,
                  }}
                >
                  {t.label}
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: t.color, fontVariantNumeric: 'tabular-nums' }}>{t.value}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  }

  const totalHours = sheets.reduce((s, x) => s + (x.totalHours || 0), 0);
  const newest = sheets[0];

  /* ── hero row: ACTUAL twins of the quoted boxes, same visual language ──
     Actual Price / Actual COGS are finance's own "AS SOLD" and "AS SOLD
     COST" figures, read verbatim, never recomputed here. Profit and margin
     follow from them (finance's own margin figure is used when present).

     SCOPE still matters for the INVOICING money: some records cover a whole
     PROGRAM (several POs across several quotes), so remaining/invoiced tiles
     show this project's own PO slice and the program totals are named as
     such. AS SOLD is a program-level figure and is labelled that way. */
  const partial = data.scope === 'partial';
  const mine = data.mine || { poValue: 0, invoiced: 0, pos: [], poCount: 0 };
  // a project routinely holds SEVERAL POs - these tiles are the sum of all of
  // them, and every PO number is listed underneath
  const minePos = mine.pos || [];
  const mineRemaining = mine.remaining != null ? mine.remaining : mine.poValue - mine.invoiced;
  // imported figures carry long float tails (11383919.7700000003) - money is cents
  const cents = (n) => (n == null ? null : Math.round(n * 100) / 100);
  const asSold = cents(sheets.reduce((s, x) => s + (x.asSold || 0), 0)) || null;
  const asSoldCost = cents(sheets.reduce((s, x) => s + (x.asSoldCost || 0), 0)) || null;
  const actualProfit = asSold != null ? cents(asSold - (asSoldCost || 0)) : null;
  // finance's own margin figure when it maps 1:1, else derived from the totals
  const sheetMargin = sheets.length === 1 && sheets[0].asSoldMargin != null ? sheets[0].asSoldMargin * 100 : null;
  const actualMargin = sheetMargin != null ? sheetMargin : asSold ? (actualProfit / asSold) * 100 : null;
  const marginColor = actualMargin === null ? '#aaa' : actualMargin >= 30 ? '#1B5E20' : actualMargin >= 20 ? '#E65100' : '#B71C1C';
  const soldTiles = [
    { label: partial ? 'As Sold (program)' : 'Actual Price (As Sold)', value: asSold != null ? fmt(asSold) : '-', color: '#0D0D0D' },
    {
      label: partial ? 'As Sold Cost (program)' : 'Actual COGS (As Sold Cost)',
      value: asSoldCost != null ? fmt(asSoldCost) : '-',
      color: '#B71C1C',
    },
    {
      label: 'Actual Profit',
      value: actualProfit != null ? fmt(actualProfit) : '-',
      color: (actualProfit || 0) >= 0 ? '#1B5E20' : '#B71C1C',
    },
    { label: 'Actual Margin', value: actualMargin === null ? '-' : `${actualMargin.toFixed(2)}%`, color: marginColor },
  ];
  /* Remaining on PO = finance's own Remaining figure, summed over EVERY PO
     line this project owns - a project often runs on several POs, and a PO
     often splits into several lines. Never derived by subtraction here:
     Remaining is finance's number, we just add it up. When no line could be
     attributed to the project but the whole record is its (scope full), the
     record's Remaining total stands in. */
  const invs = data.invoices || [];
  const cSum = (arr) => cents(arr.reduce((s, l) => s + (Number(l.remaining) || 0), 0));
  /* The PO / invoiced / remaining tiles come from the PROJECT ROW whenever it
     has them (10/8/26): since 10/1/26 GlobalOps is the source of truth, people
     invoice through Edit Project ("Add an invoice"), and the imported sheets
     never change again. Reading the sheets here, Mohammed's new invoice showed
     in the Projects table but not on the project. The As Sold tiles stay the
     sheets' - nothing in the app edits them. Same leftover rule as the table. */
  const rowPo = Number(project && (project.poValue ?? project.po_value)) || 0;
  const rowInv = Number(project && (project.invoicedAlready ?? project.invoiced_already)) || 0;
  const useRow = !!project && (rowPo > 0 || rowInv > 0);
  const rowLeft = useRow ? (leftoverOf(project) ?? 0) : null;
  const remOnPo = useRow ? rowLeft : mine.lineCount > 0 ? mineRemaining : !partial ? cSum(invs) : null;
  const shownPo = useRow ? rowPo : mine.poValue;
  const shownInv = useRow ? rowInv : mine.invoiced;
  const poWord = mine.poCount > 1 ? `${mine.poCount} POs` : 'PO';
  const tiles = partial
    ? [
        ...soldTiles.slice(0, 2),
        { label: `This project · ${poWord}`, value: shownPo ? fmt(shownPo) : '-', color: '#0D0D0D' },
        { label: 'This project · Invoiced', value: fmt(shownInv), color: '#1565C0' },
        {
          label: `Remaining on ${poWord}`,
          value: fmt(remOnPo ?? 0),
          color: remOnPo > 0 ? '#92400E' : '#1B5E20',
          bg: remOnPo > 0 ? '#FEF3C7' : '#DCEDC8',
        },
      ]
    : [
        ...soldTiles,
        {
          label: `Remaining on ${poWord}`,
          value: remOnPo != null ? fmt(remOnPo) : '-',
          color: remOnPo > 0 ? '#92400E' : '#1B5E20',
          bg: remOnPo > 0 ? '#FEF3C7' : '#DCEDC8',
        },
      ];
  return (
    <div style={{ marginTop: 12, background: '#F3F7F1', border: '1px solid #DCE7D6', borderRadius: 10, padding: '10px 12px 12px' }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: '#557052', letterSpacing: 1.6, textTransform: 'uppercase', marginBottom: 8 }}>
        Actuals
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
        {tiles.map((t) => (
          <div key={t.label} style={{ background: t.bg || '#fff', border: '1px solid #DCE7D6', borderRadius: 8, padding: '10px 14px' }}>
            <div
              style={{ fontSize: 9, fontWeight: 800, color: '#557052', letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 4 }}
            >
              {t.label}
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: t.color, fontVariantNumeric: 'tabular-nums' }}>{t.value}</div>
          </div>
        ))}
      </div>
      {minePos.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: '#557052', letterSpacing: 1.2, textTransform: 'uppercase' }}>
            {minePos.length > 1 ? `${minePos.length} POs on this project` : 'PO'}
          </span>
          {minePos.map((p) => (
            <span
              key={p.poNumber}
              title={`invoiced ${fmt(p.invoiced)} · remaining ${fmt(p.remaining)}`}
              style={{
                fontSize: 9.5,
                fontWeight: 600,
                color: '#0D0D0D',
                background: '#fff',
                border: '1px solid #DCE7D6',
                borderRadius: 12,
                padding: '2px 9px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {p.poNumber} · {fmt(p.poValue)}
              {/* the sheet's per-PO remaining is frozen at the import: shown only when the row has no invoicing of its own */}
              {!useRow && p.remaining > 0.01 && <span style={{ color: '#92400E' }}> · {fmt(p.remaining)} left</span>}
            </span>
          ))}
        </div>
      )}
      <div style={{ marginTop: 4, fontSize: 9.5, color: '#8a9a86' }}>
        {partial
          ? `As Sold / As Sold Cost cover the whole program (as of ${when(newest.modified)})${useRow ? ' · PO and invoicing are this project’s own, as edited in GlobalOps' : ' - the PO tiles sum every PO on this project'}`
          : `As Sold as of ${when(newest.modified)} · hours booked: ${hrs(totalHours)}${useRow ? ' · remaining is this project’s own, as edited in GlobalOps' : ''}`}
      </div>
    </div>
  );
}
